import { computed, ref } from "vue";
import * as Sentry from "@sentry/vue";

import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { GetProjectVersionsDocument, type GetProjectVersionsQuery, type GetProjectVersionsQueryVariables } from "@/graphql";
import { actorFrom, resolveProjectActorDirectory } from "../services/projectActors";
import { isVersioningSchemaMissing } from "../services/versioningSchema";
import { compareVersionsNewestFirst, mapVersionRow } from "../services/versionMapping";
import { mapProjectLineage, type ProjectLineage } from "../services/projectLineage";
import type { ProjectVersion } from "../types/projectVersion.types";

// The project's audit trail, read from the projectVersionNumbered view. Direct
// gaiupApolloClient (not a Vue Apollo composable) per the repo rule for composables — and so the
// unit tests can mock the client module. `unavailable` is the third state next to loading/error:
// the schema ships ahead of some environments (rollout: Hasura → API → UI, see ../ROLLOUT.md).

export function useProjectVersions() {
  const versions = ref<ProjectVersion[]>([]);
  /**
   * The chain of projects this one was copied from, nearest first — or null when it was not created
   * by a copy, and equally when the reader is not a member of the parent (RTLCA-5470/5370). Read in
   * the same document as the trail, so the origin row and the history it heads arrive together.
   */
  const lineage = ref<ProjectLineage | null>(null);
  const loading = ref<boolean>(false);
  /** A silent re-read while a list is already on screen — drives the Refresh control's busy state. */
  const refreshing = ref<boolean>(false);
  const error = ref<string | null>(null);
  const unavailable = ref<boolean>(false);

  /** Newest first — the order the timeline renders in. */
  const sortedVersions = computed<ProjectVersion[]>(() => [...versions.value].sort(compareVersionsNewestFirst));

  /**
   * The latest version is the project's current state. Informational rows are skipped: they are not
   * versions, and each carries the number of the version before it — counting them would mark a team
   * change "Current" alongside the change that actually produced that state (RTLCA-5262).
   */
  const currentVersionNumber = computed<number>(() =>
    versions.value.reduce((highest, version) => (version.informational ? highest : Math.max(highest, version.versionNumber)), 0)
  );

  const isEmpty = computed<boolean>(() => !loading.value && !error.value && !unavailable.value && versions.value.length === 0);

  // Guards the refresh paths (tab return, browser-tab return) from stacking requests on top of the
  // initial load — the trail is read-only here, so the newest in-flight answer is the only one worth having.
  let inFlight = false;

  /**
   * Read the trail. `silent` keeps the rendered list on screen while it refreshes: no loading
   * state, and a failed refresh leaves the previous list alone (reported to Sentry, retried on the
   * next reveal) rather than replacing good data with an error. Use it for refreshes, not first loads.
   *
   * Resolves `false` when the read did not land — either it was dropped because one was already in
   * flight, or it failed. Callers that the user triggered by hand (the Refresh control) say so;
   * the automatic reveal-driven refreshes ignore it and try again on the next reveal.
   */
  const load = async (projectId: string, _projectName: string, options?: { silent?: boolean }): Promise<boolean> => {
    if (!projectId || inFlight) return false;
    inFlight = true;
    const silent = options?.silent === true && versions.value.length > 0;
    if (silent) {
      refreshing.value = true;
    } else {
      loading.value = true;
      error.value = null;
      unavailable.value = false;
    }
    try {
      const [result, actors] = await Promise.all([
        gaiupApolloClient.query<GetProjectVersionsQuery, GetProjectVersionsQueryVariables>({
          query: GetProjectVersionsDocument,
          fetchPolicy: "network-only",
          context: { tolerateMissingSchema: true },
          variables: { projectId },
        }),
        resolveProjectActorDirectory(),
      ]);

      versions.value = (result.data?.projectVersionNumbered ?? [])
        .map((row) => mapVersionRow(row, projectId, (userId) => actorFrom(actors, userId)))
        .filter((version): version is ProjectVersion => version !== null);
      lineage.value = mapProjectLineage(result.data?.project_by_pk);
      unavailable.value = false;
      error.value = null;
      return true;
    } catch (e) {
      if (isVersioningSchemaMissing(e)) {
        unavailable.value = true;
      } else if (silent) {
        // Keep the list that is already on screen; the next reveal refreshes again.
        Sentry.captureException(e);
      } else {
        error.value = e instanceof Error ? e.message : "Failed to load version history";
        Sentry.captureException(e);
      }
      return false;
    } finally {
      loading.value = false;
      refreshing.value = false;
      inFlight = false;
    }
  };

  return { versions, sortedVersions, lineage, currentVersionNumber, loading, refreshing, error, unavailable, isEmpty, load };
}

export default useProjectVersions;
